import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, ShieldAlert, RefreshCw } from 'lucide-react';
import ReportStatusBadge from './ReportStatusBadge';

const TARGET_TYPES = ['user', 'post', 'comment', 'message'];
const REASONS = ['spam', 'harassment', 'nudity', 'hate', 'misinformation', 'other'];
const STATUSES = ['open', 'reviewed', 'dismissed'];

const Avatar = ({ src, name }) =>
  src ? (
    <img src={src} alt={name} className="w-8 h-8 rounded-full object-cover" />
  ) : (
    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-semibold">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
};

const snapshotPreview = (report, t) => {
  const s = report.target_snapshot || {};
  switch (report.target_type) {
    case 'user':
      return s.name || s.nickname || t('targetUser');
    case 'post':
      return s.caption ? s.caption.slice(0, 80) : (s.user_name || t('targetPost'));
    case 'comment':
      return s.text ? s.text.slice(0, 80) : t('targetComment');
    case 'message':
      return s.message ? s.message.slice(0, 80) : t('targetMessage');
    default:
      return '';
  }
};

const AdminReports = ({ token, API, t, isRTL, onBack, onOpenDetail }) => {
  const [statusFilter, setStatusFilter] = useState('open');
  const [targetType, setTargetType] = useState(null);
  const [reason, setReason] = useState(null);
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ open: 0, reviewed: 0, dismissed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (statusFilter) params.status = statusFilter;
      if (targetType) params.target_type = targetType;
      if (reason) params.reason = reason;
      const res = await axios.get(`${API}/admin/reports`, { headers, params });
      setReports(res.data.reports || []);
      setCounts(res.data.counts || { open: 0, reviewed: 0, dismissed: 0 });
    } catch (err) {
      setError(err?.response?.data?.detail || t('error'));
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [API, token, statusFilter, targetType, reason, t]);

  useEffect(() => { load(); }, [load]);

  const targetLabel = (tt) => {
    if (tt === 'user') return t('targetUser');
    if (tt === 'post') return t('targetPost');
    if (tt === 'comment') return t('targetComment');
    if (tt === 'message') return t('targetMessage');
    return tt;
  };

  return (
    <div data-testid="admin-reports-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack} data-testid="admin-reports-back" className="flex items-center gap-1 text-purple-600 font-medium">
            <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className="text-sm">{t('back')}</span>
          </button>
          <h1 className="flex items-center gap-2 text-base font-bold text-gray-900">
            <ShieldAlert className="w-4 h-4 text-purple-600" />
            {t('adminReportsTitle')}
          </h1>
          <button onClick={load} data-testid="admin-reports-refresh" className="text-purple-600">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Status tabs */}
        <div data-testid="admin-status-tabs" className="bg-white rounded-xl border border-gray-200 p-1 flex gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`admin-tab-${s}`}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                statusFilter === s
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {s === 'open' && t('statusOpen')}
              {s === 'reviewed' && t('statusReviewed')}
              {s === 'dismissed' && t('statusDismissed')}
              <span className="ml-1.5 text-xs opacity-80">({counts[s] || 0})</span>
            </button>
          ))}
        </div>

        {/* Target-type chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setTargetType(null)}
            data-testid="admin-target-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${!targetType ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200'}`}
          >
            {t('allTargets')}
          </button>
          {TARGET_TYPES.map((tt) => (
            <button
              key={tt}
              onClick={() => setTargetType(tt)}
              data-testid={`admin-target-${tt}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${targetType === tt ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200'}`}
            >
              {targetLabel(tt)}
            </button>
          ))}
        </div>

        {/* Reason chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setReason(null)}
            data-testid="admin-reason-all"
            className={`px-3 py-1 rounded-full text-[11px] font-medium border ${!reason ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}
          >
            {t('allReasons')}
          </button>
          {REASONS.map((rs) => (
            <button
              key={rs}
              onClick={() => setReason(rs)}
              data-testid={`admin-reason-${rs}`}
              className={`px-3 py-1 rounded-full text-[11px] font-medium border ${reason === rs ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}
            >
              {t(`reason_${rs}`)}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="py-16 text-center text-gray-500">{t('loading')}</div>
        ) : error ? (
          <div data-testid="admin-reports-error" className="py-12 text-center text-red-600 text-sm">{String(error)}</div>
        ) : reports.length === 0 ? (
          <div data-testid="admin-reports-empty" className="py-16 text-center text-gray-500">{t('noReports')}</div>
        ) : (
          <ul data-testid="admin-reports-list" className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {reports.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-report-row-${r.id}`}
                onClick={() => onOpenDetail(r)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <Avatar src={r.reporter?.profile_picture_url} name={r.reporter?.name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {r.reporter?.name || t('unknownUser')}
                      </span>
                      <span className="text-xs text-gray-500">{t('reportedAVerb')}</span>
                      <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                        {targetLabel(r.target_type)}
                      </span>
                      <ReportStatusBadge status={r.status} t={t} />
                    </div>
                    <p className="mt-1 text-sm text-gray-700 truncate">
                      <span className="font-medium">{t(`reason_${r.reason}`) || r.reason}</span>
                      {' — '}
                      <span className="text-gray-500">{snapshotPreview(r, t)}</span>
                    </p>
                    <p className="mt-1 text-[11px] text-gray-400">{formatDate(r.created_at)}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
